import { useState, useCallback } from 'react';
import { SparkIcon } from '../SparkIcon';
import type {
  BreadcrumbItemData,
  BreadcrumbType,
  BreadcrumbSeparatorShape,
  BreadcrumbChevronState,
  BreadcrumbProps,
} from './Breadcrumb.types';
import './Breadcrumb.css';

type BreadcrumbSeparatorType = 'default' | 'primary' | 'brand';

function separatorTypeForIndex(
  itemIndex: number,
  itemCount: number,
  breadcrumbType: BreadcrumbType,
): BreadcrumbSeparatorType {
  if (itemIndex !== itemCount - 1) {
    return 'default';
  }
  return breadcrumbType === 'steps' ? 'brand' : 'primary';
}

export function BreadcrumbChevron({
  open = false,
  state = 'default',
  onClick,
}: {
  open?: boolean;
  state?: BreadcrumbChevronState;
  onClick?: () => void;
}) {
  return (
    <span
      className={`spark-breadcrumb-chevron spark-breadcrumb-chevron-${state}${open ? ' spark-breadcrumb-chevron-open' : ''}`}
      aria-hidden="true"
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? (e) => { e.preventDefault(); e.stopPropagation(); onClick(); } : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClick(); } } : undefined}
    >
      <SparkIcon slug={open ? 'chevron-up' : 'chevron-down'} />
    </span>
  );
}

function BreadcrumbSeparator({
  shape,
  separatorType,
}: {
  shape: BreadcrumbSeparatorShape;
  separatorType: BreadcrumbSeparatorType;
}) {
  return (
    <span
      className={`spark-breadcrumb-separator spark-breadcrumb-separator-${separatorType} spark-breadcrumb-separator-${shape}`}
      aria-hidden="true"
    >
      {shape === 'chevron' ? <SparkIcon slug="chevron-right" size="small" /> : '/'}
    </span>
  );
}

function BreadcrumbLink({
  item,
  itemType,
  chevronOpen,
  onChevronClick,
}: {
  item: BreadcrumbItemData;
  itemType: BreadcrumbType;
  chevronOpen?: boolean;
  onChevronClick?: () => void;
}) {
  const linkClass =
    itemType === 'steps'
      ? 'spark-breadcrumb-item spark-breadcrumb-item-link spark-breadcrumb-item-link-steps'
      : 'spark-breadcrumb-item spark-breadcrumb-item-link spark-breadcrumb-item-link-navigation';

  return (
    <a href={item.href ?? '#'} className={linkClass} onClick={item.onClick}>
      {item.icon && <span className="spark-breadcrumb-icon">{item.icon}</span>}
      <span className="spark-breadcrumb-label">{item.label}</span>
      {item.chevron && (
        <BreadcrumbChevron
          open={chevronOpen ?? item.chevronOpen}
          state="default"
          onClick={onChevronClick}
        />
      )}
    </a>
  );
}

function BreadcrumbCurrent({
  item,
  itemType,
  chevronOpen,
  onChevronClick,
}: {
  item: BreadcrumbItemData;
  itemType: BreadcrumbType;
  chevronOpen?: boolean;
  onChevronClick?: () => void;
}) {
  const activeClass =
    itemType === 'steps'
      ? 'spark-breadcrumb-item-active-steps'
      : 'spark-breadcrumb-item-active';

  return (
    <span className={`spark-breadcrumb-item ${activeClass}`} aria-current="page">
      {item.icon && <span className="spark-breadcrumb-icon">{item.icon}</span>}
      <span className="spark-breadcrumb-label">{item.label}</span>
      {item.chevron && (
        <BreadcrumbChevron
          open={chevronOpen ?? item.chevronOpen}
          state="active"
          onClick={onChevronClick}
        />
      )}
    </span>
  );
}

export function Breadcrumb({
  items,
  type = 'navigation',
  separatorShape = 'slash',
  onChevronClick,
  className = '',
}: BreadcrumbProps) {
  const clampedItems = items.slice(0, 4);

  const [chevronOpenState, setChevronOpenState] = useState<Record<number, boolean>>({});

  const handleChevronClick = useCallback(
    (item: BreadcrumbItemData, index: number) => {
      setChevronOpenState((prev) => {
        const current = prev[index] ?? item.chevronOpen ?? false;
        return { ...prev, [index]: !current };
      });
      onChevronClick?.(item, index);
    },
    [onChevronClick],
  );

  return (
    <nav aria-label="Breadcrumb" className={`spark-breadcrumb${className ? ` ${className}` : ''}`}>
      <ol className="spark-breadcrumb-list">
        {clampedItems.map((item, index) => {
          const isLast = index === clampedItems.length - 1;
          const separatorType = separatorTypeForIndex(index, clampedItems.length, type);
          const resolvedChevronOpen = chevronOpenState[index] ?? item.chevronOpen;
          return (
            <li key={index} className="spark-breadcrumb-item-wrapper">
              {index > 0 && (
                <BreadcrumbSeparator shape={separatorShape} separatorType={separatorType} />
              )}
              {isLast ? (
                <BreadcrumbCurrent
                  item={item}
                  itemType={type}
                  chevronOpen={resolvedChevronOpen}
                  onChevronClick={item.chevron ? () => handleChevronClick(item, index) : undefined}
                />
              ) : (
                <BreadcrumbLink
                  item={item}
                  itemType={type}
                  chevronOpen={resolvedChevronOpen}
                  onChevronClick={item.chevron ? () => handleChevronClick(item, index) : undefined}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
